import { TournamentState } from '@ps/poker-games-tile-mappers';
import type {
  PokerMttQueryVariables,
  PokerRelatedEvents,
  PokerSundayMillion
} from '@ps/web-martech-cms-types';
import type { Selector } from 'reselect';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  CommonQueryVariableOptions,
  ConfigQueryVariablesPayload,
  Tournament
} from '@swf/poker';
import { generateMTTSelectors, mapQueryVariables } from '@swf/poker';

import type { MainEventTournament } from '../types';
import { SundayMillionTab } from '../types';
import getMainEventTournament from '../utils/getMainEventTournament';
import mapRelatedEvents from '../utils/mapRelatedEvents';

import type { PokerSundayMillionState } from './types';

export const selectors = (
  slice: Slicer<PokerSundayMillionState>,
  createSelector: SelectorCreator
) => ({
  ...generateMTTSelectors<PokerSundayMillionState>(slice),
  isHydrated: () => slice((state) => state.isHydrated),
  isLoading: () => slice((state) => state.isLoading),
  getTournament(this: { getTournamentList: Selector<RootState, Tournament[]> }) {
    return createSelector(
      slice,
      this.getTournamentList,
      (_, tournaments): MainEventTournament => getMainEventTournament(tournaments)
    );
  },
  getSelectedTab: () => slice((state) => state.selectedTab),
  getTimerDate(this: { getTournament: Selector<RootState, MainEventTournament | undefined> }) {
    return createSelector(slice, this.getTournament, (_, tournament) => {
      switch (tournament?.formattedState) {
        case TournamentState.COMPLETED:
          return tournament.timeClosed;
        case TournamentState.LATE_REGISTRATION:
          return tournament.regEndInt;
        case TournamentState.PAUSED:
          return tournament.resumeTimeInt;
        default:
          return tournament?.whenStartInt;
      }
    });
  },
  getRelatedEvents(this: { getSelectedTab: Selector<RootState, SundayMillionTab> }) {
    return createSelector(
      slice,
      this.getSelectedTab,
      (rootState: RootState, widget: PokerSundayMillion) =>
        (widget.related_events || [])
          .map(
            (event) =>
              select.cms.getContentItem(rootState, event.uid) as PokerRelatedEvents | undefined
          )
          .filter((event): event is PokerRelatedEvents => !!event),
      (_, selectedTab, relatedEvents): PokerRelatedEvents[] =>
        mapRelatedEvents(relatedEvents, selectedTab === SundayMillionTab.Main)
    );
  },
  getTableRowsCount(this: { getTournamentList: Selector<RootState, Tournament[]> }) {
    return createSelector(
      slice,
      this.getTournamentList,
      (_, tournaments): number => tournaments?.length
    );
  },
  getConfigQueryVariables(this: {
    getCommonVariables: Selector<RootState, CommonQueryVariableOptions>;
  }) {
    return createSelector(
      slice,
      this.getCommonVariables,
      (rootState: RootState, { widget }: ConfigQueryVariablesPayload<PokerSundayMillion>) => {
        const isMainEvent = rootState[widget.uid].selectedTab === SundayMillionTab.Main;
        const uid = isMainEvent
          ? widget.main_event_filter_variables?.[0]?.uid
          : widget.satellite_filter_variables?.[0]?.uid;
        return select.cms.getContentItem(rootState, uid) as PokerMttQueryVariables | undefined;
      },
      (_, commonVariables, cmsVariables) =>
        mapQueryVariables({ ...commonVariables, excludeSnG: false }, cmsVariables)
    );
  }
});
